import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  useDeferredValue,
  useTransition,
} from 'react';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { UserStatus } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import {
  SearchBar,
  FilterDropdown,
  AppliedFilters,
} from '@/components/filters';
import {
  createWorkerFilterConfig,
  WORKER_FILTER_KEYS,
} from '@/components/worker/utils/workerFilters.utils';
import { useWorkerColumns } from '@/components/worker/hooks/useWorkerColumns';
import { Skeleton, SkeletonTable } from '@/components/shared/Skeleton';
import { useColumnResize } from '@/components/job/useColumnResize';
import { ResizeHandle } from '@/components/shared/ResizeHandle';
import { useTenantId } from '@/contexts/TenantContext';
import { formatUserDisplayName } from '@/utils/utils';

type SortField =
  | 'candidate'
  | 'email'
  | 'status'
  | 'onboardingPercentage'
  | 'industry'
  | 'state'
  | 'suburb'
  | 'postcode'
  | 'transport'
  | 'gender'
  | 'startDate'
  | 'lastUpdatedAt'
  | 'lastJob'
  | 'jobsCompleted'
  | 'assignedAdmin'
  | null;
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 20;

const Workers = () => {
  const { workers, fetchWorkersForTenant, isLoading } = useWorkerStore();
  const { tenantUsers, fetchTenantUsersForTenant, getTenantUserById } =
    useTenantStore();
  const tenantId = useTenantId();
  const {
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
    fetchClassificationGroups,
  } = useClassificationStore();
  const { getTerm } = useTemplateStore();
  const {
    columns,
    getWorkerIndustryNames: getWorkerIndustryNamesWithParams,
    getWorkerTransport,
    getWorkerGender,
    getWorkerJobInfo,
  } = useWorkerColumns();

  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTableUpdatePending, startTableUpdate] = useTransition();
  const [filters, setFilters] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(WORKER_FILTER_KEYS.map((key) => [key, []])),
  );

  const deferredFilters = useDeferredValue(filters);
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const isTableUpdating =
    isTableUpdatePending ||
    deferredFilters !== filters ||
    deferredSearchQuery !== searchQuery;

  const setFilterByKey = useCallback((key: string) => {
    return (values: string[]) => {
      startTableUpdate(() => {
        setCurrentPage(1);
        setFilters((previousFilters) => ({
          ...previousFilters,
          [key]: values,
        }));
      });
    };
  }, []);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    },
    [currentPage],
  );

  const classificationStoreParams = useMemo(
    () => ({
      classificationGroups: classificationGroups ?? [],
      getClassificationById,
      getClassificationLevelById,
    }),
    [classificationGroups, getClassificationById, getClassificationLevelById],
  );

  const tenantUserFilterParams = useMemo(
    () => ({
      // Only show active tenant users as available options.
      tenantUsers:
        tenantUsers?.filter((u) => u.status === UserStatus.Active) ?? [],
      getTenantUserById,
    }),
    [tenantUsers, getTenantUserById],
  );

  const workerFilterConfig = useMemo(
    () =>
      createWorkerFilterConfig(
        classificationStoreParams,
        tenantUserFilterParams,
      ),
    [classificationStoreParams, tenantUserFilterParams],
  );

  useEffect(() => {
    if (tenantId) {
      fetchClassificationGroups(tenantId);
      fetchWorkersForTenant(tenantId);
      fetchTenantUsersForTenant(tenantId);
    }
  }, [
    tenantId,
    fetchClassificationGroups,
    fetchWorkersForTenant,
    fetchTenantUsersForTenant,
    workers?.length,
  ]);

  const handleSort = (field: SortField) => {
    startTableUpdate(() => {
      if (field === sortField) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    });
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↑↓';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleCandidateClick = (workerId: string) => {
    window.open(`/workers/${workerId}`, '_blank', 'noopener,noreferrer');
  };

  // Build defaultWidths from column config
  const defaultWidths = columns.reduce(
    (acc, col) => {
      acc[col.key] = col.width;
      return acc;
    },
    {} as Record<string, number>,
  );

  const { columnWidths, handleMouseDown } = useColumnResize({
    defaultWidths,
    minWidth: 80,
  });

  const tableRef = useRef<HTMLTableElement>(null);

  const startIndex = useMemo(() => {
    return (currentPage - 1) * ITEMS_PER_PAGE + 1;
  }, [currentPage]);

  const filteredAndSortedWorkers = useMemo(() => {
    return (
      workers
        ?.filter((worker) => {
          const name =
            `${worker.firstName} ${worker.lastName}`.toLowerCase() || '';
          const email = worker.email?.toLowerCase() || '';
          const status = worker.status?.toLowerCase() || '';
          const query = deferredSearchQuery.toLowerCase();
          const matchesSearch =
            name.includes(query) ||
            email.includes(query) ||
            status.includes(query);

          const selectedStatusFilters = deferredFilters.status ?? [];
          const hasStatusFilterSelected = selectedStatusFilters.length > 0;
          const isDeletedWorker =
            worker.status?.toLowerCase() === UserStatus.Deleted.toLowerCase();
          if (!hasStatusFilterSelected && isDeletedWorker) {
            return false;
          }

          const matchesAllFilters = workerFilterConfig.every((config) => {
            const filterValues = deferredFilters[config.key];
            if (!filterValues?.length) return true;

            const workerValues = config.getValueFromItem(worker);
            return workerValues.some((v) => filterValues.includes(v));
          });

          return matchesSearch && matchesAllFilters;
        })
        .sort((a, b) => {
          if (!sortField) return 0;

          const direction = sortDirection === 'asc' ? 1 : -1;

          switch (sortField) {
            case 'candidate':
              return (
                direction *
                ((a.firstName || '') + ' ' + (a.lastName || '')).localeCompare(
                  (b.firstName || '') + ' ' + (b.lastName || ''),
                )
              );
            case 'email':
              return direction * (a.email || '').localeCompare(b.email || '');
            case 'status':
              return direction * (a.status || '').localeCompare(b.status || '');
            case 'industry': {
              const aIndustries = getWorkerIndustryNamesWithParams(a)
                .slice()
                .sort()
                .join(', ');
              const bIndustries = getWorkerIndustryNamesWithParams(b)
                .slice()
                .sort()
                .join(', ');
              return direction * aIndustries.localeCompare(bIndustries);
            }
            case 'state':
              return (
                direction *
                (a.address?.state || '').localeCompare(b.address?.state || '')
              );
            case 'suburb':
              return (
                direction *
                (a.address?.suburb || '').localeCompare(b.address?.suburb || '')
              );
            case 'postcode':
              return (
                direction *
                (a.address?.postcode || '').localeCompare(
                  b.address?.postcode || '',
                )
              );
            case 'transport':
              return (
                direction *
                getWorkerTransport(a).localeCompare(getWorkerTransport(b))
              );
            case 'gender':
              return (
                direction * getWorkerGender(a).localeCompare(getWorkerGender(b))
              );
            case 'startDate': {
              const aStartDate = a.cognitoCreatedAt || '';
              const bStartDate = b.cognitoCreatedAt || '';
              return direction * aStartDate.localeCompare(bStartDate);
            }
            case 'lastUpdatedAt': {
              const aLastUpdatedAt = a.updatedAt || '';
              const bLastUpdatedAt = b.updatedAt || '';
              return direction * aLastUpdatedAt.localeCompare(bLastUpdatedAt);
            }
            case 'lastJob': {
              const aJobInfo = getWorkerJobInfo(a.id);
              const bJobInfo = getWorkerJobInfo(b.id);
              const aLastJob = aJobInfo.lastJobEndDate || '';
              const bLastJob = bJobInfo.lastJobEndDate || '';
              return direction * aLastJob.localeCompare(bLastJob);
            }
            case 'jobsCompleted': {
              const aJobInfo = getWorkerJobInfo(a.id);
              const bJobInfo = getWorkerJobInfo(b.id);
              return (
                direction * (aJobInfo.completedCount - bJobInfo.completedCount)
              );
            }
            case 'onboardingPercentage': {
              const aVal = a.onboardingPercentage || '-';
              const bVal = b.onboardingPercentage || '-';
              const aNum = aVal === '-' ? -1 : Number(aVal);
              const bNum = bVal === '-' ? -1 : Number(bVal);
              return direction * (aNum - bNum);
            }
            case 'assignedAdmin': {
              const tenantUserA = a.assignedAdminId
                ? getTenantUserById(a.assignedAdminId)
                : null;
              const tenantUserB = b.assignedAdminId
                ? getTenantUserById(b.assignedAdminId)
                : null;

              const aVal = tenantUserA
                ? formatUserDisplayName(tenantUserA)
                : '-';
              const bVal = tenantUserB
                ? formatUserDisplayName(tenantUserB)
                : '-';
              return direction * aVal.localeCompare(bVal);
            }
            default:
              return 0;
          }
        }) ?? []
    );
  }, [
    workers,
    deferredSearchQuery,
    workerFilterConfig,
    deferredFilters,
    sortField,
    sortDirection,
    getWorkerIndustryNamesWithParams,
    getWorkerTransport,
    getWorkerGender,
    getWorkerJobInfo,
    getTenantUserById,
  ]);

  const totalWorkerCount = filteredAndSortedWorkers.length;
  const totalPages = Math.ceil(totalWorkerCount / ITEMS_PER_PAGE);

  useEffect(() => {
    const maxPage = Math.max(1, totalPages);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, totalPages]);

  const endIndex = useMemo(() => {
    return Math.min(startIndex + ITEMS_PER_PAGE - 1, totalWorkerCount);
  }, [startIndex, totalWorkerCount]);

  const filteredWorkers = useMemo(
    () => filteredAndSortedWorkers.slice(startIndex - 1, endIndex),
    [filteredAndSortedWorkers, startIndex, endIndex],
  );

  // Show skeleton loading while data is being fetched
  if (isLoading && workers?.length === 0) {
    return (
      <div className="flex h-full flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <Skeleton height="h-8" width="w-24" />
          <Skeleton height="h-10" width="w-36" />
        </div>

        <div className="mb-2 flex items-center gap-2">
          <Skeleton height="h-10" width="w-64" />
          <Skeleton height="h-10" width="w-32" />
        </div>

        <SkeletonTable rows={8} columns={14} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">
          {getTerm(TemplateTerm.WORKER, true)}
        </h2>
      </div>

      <div className="mb-2 flex items-center gap-2">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={handleSearchQueryChange}
        />
        {workerFilterConfig.map((config) => (
          <FilterDropdown
            key={config.key}
            title={config.title}
            data={
              config.key === 'status'
                ? (workers ?? [])
                : filteredAndSortedWorkers
            }
            getOptions={config.getOptions}
            selectedFilters={filters[config.key] ?? []}
            setSelectedFilters={setFilterByKey(config.key)}
          />
        ))}
        {isTableUpdating && (
          <span className="text-xs text-gray-500">Updating results...</span>
        )}
      </div>
      <AppliedFilters
        filterGroups={workerFilterConfig.map(({ key, title }) => ({
          label: title,
          values: filters[key] ?? [],
          setValues: setFilterByKey(key),
        }))}
      />

      <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
        <table
          ref={tableRef}
          className="w-full border-collapse"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            {columns.map((col) => (
              <col
                key={col.key}
                style={{ width: `${columnWidths[col.key]}px` }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() =>
                    col.sortable && handleSort(col.key as SortField)
                  }
                  className={`relative px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase hover:bg-gray-50 ${
                    col.sortable ? 'cursor-pointer' : ''
                  }`}
                  style={{ width: `${columnWidths[col.key]}px` }}
                >
                  <div className="flex items-center">
                    {col.title}
                    {col.sortable && (
                      <span className="ml-2 text-gray-400">
                        {getSortIcon(col.key as SortField)}
                      </span>
                    )}
                  </div>
                  <ResizeHandle
                    column={col.key}
                    onMouseDown={handleMouseDown}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && filteredWorkers?.length === 0 ? (
              // Show skeleton rows for loading
              Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  {columns.map((col, colIndex) => (
                    <td key={col.key} className={col.cellClassName}>
                      {colIndex === 0 && <Skeleton height="h-4" width="w-32" />}
                      {colIndex === 1 && <Skeleton height="h-4" width="w-40" />}
                      {colIndex === 2 && (
                        <Skeleton height="h-6" width="w-16" rounded-sm="full" />
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredWorkers?.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border-t border-gray-200 px-4 py-6 text-center text-gray-500"
                >
                  No workers found
                </td>
              </tr>
            ) : (
              filteredWorkers?.map((worker) => (
                <tr
                  key={worker.id}
                  onClick={() => handleCandidateClick(worker.id)}
                  className="cursor-pointer transition-colors hover:bg-gray-100"
                >
                  {columns.map((col) => (
                    <td key={col.key} className={col.cellClassName}>
                      {col.renderCell(worker)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          <span>
            Showing {totalWorkerCount === 0 ? 0 : startIndex} to {endIndex} of{' '}
            {totalWorkerCount} results
          </span>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= (totalPages || 0)}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Workers;
